/**
 * Transition Rules: EnergyPlus 9.5 to 9.6
 *
 * Major changes: Insert blank field for surface objects, ventilation procedure rename
 */

import type { VersionTransition, ObjectTransitionRule } from '../types';
import { createCopyMappings, createShiftedCopyMappings } from '../types';

// Helper to create surface object rule (insert blank field after position 3 or 4)
function createSurfaceInsertRule(className: string, insertAfter: number): ObjectTransitionRule {
  return {
    sourceClass: className,
    targetClass: className,
    fieldMappings: [
      ...createCopyMappings(0, insertAfter),
      { sourceIndex: -1, targetIndex: insertAfter, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(insertAfter, insertAfter + 1, 200), // Surfaces can have many vertices
    ],
  };
}

export function createTransition_9_5_to_9_6(): VersionTransition {
  const objectRules = new Map<string, ObjectTransitionRule>();
  const variableMappings = new Map<string, string>();
  const deletedObjects = new Set<string>();
  const renamedObjects = new Map<string, string>();

  // Surface objects - Insert blank field after field 3 (index 3) or 4 (index 4)
  const surfacesInsertAt3 = [
    'CEILING:ADIABATIC',
    'CEILING:INTERZONE',
    'FLOOR:DETAILED',
    'FLOOR:GROUNDCONTACT',
    'FLOOR:ADIABATIC',
    'FLOOR:INTERZONE',
    'INTERNALMASS',
    'ROOFCEILING:DETAILED',
    'ROOF',
    'WALL:DETAILED',
    'WALL:EXTERIOR',
    'WALL:ADIABATIC',
    'WALL:UNDERGROUND',
    'WALL:INTERZONE',
  ];

  for (const surface of surfacesInsertAt3) {
    objectRules.set(surface, createSurfaceInsertRule(surface, 3));
  }

  // BuildingSurface:Detailed - Insert blank field after field 4
  objectRules.set('BUILDINGSURFACE:DETAILED', createSurfaceInsertRule('BuildingSurface:Detailed', 4));

  // AirflowNetwork:MultiZone:ReferenceCrackConditions - Default field 2 to "20.0"
  objectRules.set('AIRFLOWNETWORK:MULTIZONE:REFERENCECRACKCONDITIONS', {
    sourceClass: 'AirflowNetwork:MultiZone:ReferenceCrackConditions',
    targetClass: 'AirflowNetwork:MultiZone:ReferenceCrackConditions',
    fieldMappings: [
      { sourceIndex: 0, targetIndex: 0, type: 'copy' },
      {
        sourceIndex: 1,
        targetIndex: 1,
        type: 'transform',
        transform: (value) => value.trim() === '' ? '20.0' : value,
      },
      ...createShiftedCopyMappings(2, 2, 10),
    ],
  });

  // AirLoopHVAC:OutdoorAirSystem - Remove field 4 if present
  objectRules.set('AIRLOOPHVAC:OUTDOORAIRSYSTEM', {
    sourceClass: 'AirLoopHVAC:OutdoorAirSystem',
    targetClass: 'AirLoopHVAC:OutdoorAirSystem',
    fieldMappings: [
      ...createCopyMappings(0, 3), // Fields 0-2
      { sourceIndex: 3, targetIndex: -1, type: 'delete' },
      ...createShiftedCopyMappings(4, 3, 10),
    ],
  });

  // Controller:MechanicalVentilation - Rename VentilationRateProcedure
  objectRules.set('CONTROLLER:MECHANICALVENTILATION', {
    sourceClass: 'Controller:MechanicalVentilation',
    targetClass: 'Controller:MechanicalVentilation',
    fieldMappings: [
      ...createCopyMappings(0, 3), // Fields 0-2
      {
        sourceIndex: 3,
        targetIndex: 3,
        type: 'transform',
        transform: (value) =>
          value.toUpperCase() === 'VENTILATIONRATEPROCEDURE'
            ? 'Standard62.1VentilationRateProcedure'
            : value,
      },
      ...createShiftedCopyMappings(4, 4, 200),
    ],
  });

  // Sizing:System - Rename VentilationRateProcedure (field 27)
  objectRules.set('SIZING:SYSTEM', {
    sourceClass: 'Sizing:System',
    targetClass: 'Sizing:System',
    fieldMappings: [
      ...createCopyMappings(0, 26), // Fields 0-25
      {
        sourceIndex: 26,
        targetIndex: 26,
        type: 'transform',
        transform: (value) =>
          value.toUpperCase() === 'VENTILATIONRATEPROCEDURE'
            ? 'Standard62.1VentilationRateProcedure'
            : value,
      },
      ...createShiftedCopyMappings(27, 27, 30),
    ],
  });

  // GroundHeatExchanger:System - Insert field at position 10
  objectRules.set('GROUNDHEATEXCHANGER:SYSTEM', {
    sourceClass: 'GroundHeatExchanger:System',
    targetClass: 'GroundHeatExchanger:System',
    fieldMappings: [
      ...createCopyMappings(0, 9), // Fields 0-8
      {
        sourceIndex: 8,
        targetIndex: 9,
        type: 'transform',
        transform: (value, _obj) => value.trim() === '' ? 'UHFCalc' : value,
      },
      ...createShiftedCopyMappings(9, 10, 50),
    ],
  });

  // PerformancePrecisionTradeoffs - Mode06 → Mode07, Mode07 → Mode08
  objectRules.set('PERFORMANCEPRECISIONTRADEOFFS', {
    sourceClass: 'PerformancePrecisionTradeoffs',
    targetClass: 'PerformancePrecisionTradeoffs',
    fieldMappings: [
      ...createCopyMappings(0, 2), // Fields 0-1
      {
        sourceIndex: 2,
        targetIndex: 2,
        type: 'transform',
        transform: (value) => {
          const upper = value.toUpperCase();
          if (upper === 'MODE06') return 'Mode07';
          if (upper === 'MODE07') return 'Mode08';
          return value;
        },
      },
      ...createShiftedCopyMappings(3, 3, 10),
    ],
  });

  return {
    fromVersion: { major: 9, minor: 5 },
    toVersion: { major: 9, minor: 6 },
    objectRules,
    variableMappings,
    deletedObjects,
    renamedObjects,
  };
}
